import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import BulkImport from "@/components/BulkImport";
import { toast } from "sonner";
import { Trash2, Pencil } from "lucide-react";

export default function PitmanAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", chapter: "", content: "", time_limit_seconds: 900 });
  const [imageFiles, setImageFiles] = useState([]);       // File[] chosen but not yet uploaded
  const [uploadedUrls, setUploadedUrls] = useState([]);   // URLs from backend after upload
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const load = () => api.get("/pitman-exercises").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const onPickImages = (e) => {
    const list = Array.from(e.target.files || []);
    if (list.length > 10) { toast.error("Max 10 images per exercise"); return; }
    setImageFiles(list);
    setUploadedUrls([]);
  };

  const uploadImages = async () => {
    if (imageFiles.length === 0) return;
    setUploading(true);
    try {
      const fd = new FormData();
      imageFiles.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/admin/pitman-exercises/upload-images", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadedUrls(data.urls || []);
      toast.success(`${data.urls.length} image(s) uploaded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const add = async (e) => {
    e.preventDefault();
    if (uploadedUrls.length === 0 && imageFiles.length > 0) {
      toast.error("Click 'Upload images' first"); return;
    }
    setBusy(true);
    try {
      const payload = { ...form, images: (uploadedUrls.length ? uploadedUrls : (form.images || [])) };
      if (editingId) {
        await api.patch(`/admin/pitman-exercises/${editingId}`, payload);
        toast.success("Exercise updated");
      } else {
        await api.post("/admin/pitman-exercises", payload);
        toast.success("Exercise added");
      }
      setForm({ title: "", chapter: "", content: "", time_limit_seconds: 900 });
      setImageFiles([]);
      setUploadedUrls([]);
      setEditingId(null);
      load();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const editPitman = (p) => {
    setEditingId(p.id);
    setForm({
      title: p.title || "", chapter: p.chapter || "",
      content: p.content || "",
      time_limit_seconds: p.time_limit_seconds || 900,
      images: p.images || [],
    });
    setUploadedUrls(p.images || []);
    setImageFiles([]);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancelPitmanEdit = () => {
    setEditingId(null);
    setForm({ title: "", chapter: "", content: "", time_limit_seconds: 900 });
    setImageFiles([]);
    setUploadedUrls([]);
  };

  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/admin/pitman-exercises/${id}`);
    if (editingId === id) cancelPitmanEdit();
    load();
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <form onSubmit={add} className="border border-black/20 p-6 space-y-4" data-testid="admin-pitman-form">
        <div className="text-xs uppercase tracking-[0.3em] font-bold">Add Pitman exercise</div>
        <Input required placeholder="Title (e.g. Exercise 121)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-none font-mono" data-testid="admin-pitman-title" />
        <Input required placeholder="Chapter (e.g. Chapter 12)" value={form.chapter} onChange={(e) => setForm({ ...form, chapter: e.target.value })} className="rounded-none font-mono" data-testid="admin-pitman-chapter" />

        {/* Shorthand image upload */}
        <div className="border border-dashed border-black/30 p-3 space-y-2">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">Shorthand pages (1–10 images)</div>
          <Input
            type="file" accept="image/*" multiple onChange={onPickImages}
            className="rounded-none font-mono" data-testid="admin-pitman-image-input"
          />
          {imageFiles.length > 0 && uploadedUrls.length === 0 && (
            <Button
              type="button" onClick={uploadImages} disabled={uploading}
              className="rounded-none h-9 px-4 bg-white text-[#002FA7] border-2 border-[#002FA7] hover:bg-[#F4F4F5] text-xs uppercase tracking-[0.2em] font-bold w-full"
              data-testid="admin-pitman-image-upload-btn"
            >
              {uploading ? "Uploading…" : `Upload ${imageFiles.length} image(s)`}
            </Button>
          )}
          {uploadedUrls.length > 0 && (
            <div className="grid grid-cols-4 gap-2" data-testid="admin-pitman-image-thumbs">
              {uploadedUrls.map((u, i) => (
                <div key={u} className="border border-black/15 aspect-square overflow-hidden bg-neutral-50">
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${u}`} alt={`page ${i + 1}`} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          )}
        </div>

        <Textarea required placeholder="Transcription (English answer key — accuracy is checked against this)" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="rounded-none font-mono min-h-40" data-testid="admin-pitman-content" />

        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">Time limit (minutes)</Label>
          <Input
            type="number" min="1" max="120"
            value={Math.round(form.time_limit_seconds / 60)}
            onChange={(e) => setForm({ ...form, time_limit_seconds: Math.max(60, Math.min(7200, Number(e.target.value) * 60)) })}
            className="rounded-none font-mono mt-1"
            data-testid="admin-pitman-time-limit"
          />
        </div>

        <Button type="submit" disabled={busy} className={`rounded-none w-full h-11 ${editingId ? "bg-brand-crimson hover:bg-brand-crimson/90" : "bg-[#0A2540] hover:bg-[#002FA7]"} text-white text-xs uppercase tracking-[0.2em] font-bold`} data-testid="admin-pitman-submit-btn">
          {busy ? (editingId ? "Updating…" : "Adding…") : (editingId ? "Update exercise" : "Add exercise")}
        </Button>
        {editingId && (
          <Button type="button" onClick={cancelPitmanEdit} variant="outline" className="rounded-none w-full h-9 border-black/30 text-neutral-700 hover:bg-neutral-100 text-[10px] uppercase tracking-widest font-bold" data-testid="admin-pitman-cancel-edit-btn">
            Cancel edit
          </Button>
        )}
      </form>

      <div>
        <h3 className="text-lg font-black tracking-tight mb-3">Existing exercises</h3>
        <div className="border border-black/15 divide-y divide-black/10" data-testid="admin-pitman-list">
          {items.length === 0 && <div className="p-4 font-mono text-sm text-neutral-500">None yet.</div>}
          {items.map((p) => (
            <div key={p.id} className="flex items-center justify-between p-4">
              <div>
                <div className="font-bold">{p.title}</div>
                <div className="text-xs font-mono text-neutral-500">
                  {p.chapter} · {p.word_count} words
                  {p.images?.length ? ` · ${p.images.length} image(s)` : " · no images"}
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => editPitman(p)} variant="outline" size="sm" className="rounded-none border-brand-gold text-brand-gold hover:bg-brand-gold hover:text-white" data-testid={`admin-pitman-edit-${p.id}`}>
                  <Pencil className="w-3 h-3 mr-1" /> Edit
                </Button>
                <Button onClick={() => del(p.id)} variant="outline" size="sm" className="rounded-none border-red-300 text-red-700 hover:bg-red-50" data-testid={`admin-pitman-delete-${p.id}`}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="md:col-span-2">
        <BulkImport
          module="pitman"
          requiredFields={["title", "chapter", "content"]}
          onImported={load}
        />
      </div>
    </div>
  );
}
